// The sea (art/model-integration.md 9.3 water): one lit sheet at the
// waterline that knows how deep it is. Over a beach it runs clear enough to
// show the sand, then turquoise, teal and at last the deep blue the game has
// always had; a line of surf rolls in on every shore; it catches the sky at a
// low angle and throws back the sun (or the moon) in a glitter path off its
// ripples.
//
// It replaces buildSky's translucent disc and the dark slab under it. The
// smooth ground stops drawing a quad wholly under water, so under the old
// see-through sea every coast wore a saw-toothed band of pale water — the
// crossing quads' underwater halves, cut off where the dropped quads began —
// and past it the flat slab 15 m down. This sea paints its own bottom: it
// reads the ground's height under every pixel from a texture of the same 12 m
// grid corners the chunks are built from (terrain_core.js cornerHeight), so
// the colour of the shallows and the surf line follow the drawn shore to the
// metre, and it is opaque, so nothing under it needs drawing at all.
//
//   The depth window  — a 512-corner square of ground heights (±3 km at 12 m)
//                       that follows the camera, filled one corner-cache tile
//                       at a time, nearest first, on a small per-frame ration.
//                       The texture wraps, so a window that has slid along
//                       only rewrites the tiles it slid onto. Past the window
//                       — which is past where the ground is drawn — the sea
//                       is open water, as it always was.
//   The ripples       — a tileable swell baked here at boot (no texture to
//                       fetch): its slopes, sampled at two scales drifting
//                       with the wind, tilt the light.
//   The light         — Phong: the sun (or moon) and the sky fill light the
//                       body of the water like everything else in the world,
//                       the key light's glint rides the ripples, and the sky
//                       is laid over it by Fresnel — dark water underfoot, a
//                       sheet of sky out toward the horizon.
//
// Every tier draws it, in three weights (WATER_QUALITY): phones and software
// GL a single swell, which prices out level with the two see-through layers
// it replaced; other GPUs the full sea; tier 2's film light a glint bright
// enough to bloom.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";

// --- the ripples -------------------------------------------------------------

/** Texels a side of the ripple map. */
export const RIPPLE_SIZE = 256;
// Sine trains summed into the swell: enough that no single crest line shows.
const RIPPLE_WAVES = 28;

/**
 * The ripple map: a tileable swell as RGBA bytes. R and G are the height's
 * slope along the tile's x and y (0.5 is flat; the steepest texel in the tile
 * sits just inside the byte's range), B the height itself (0..1), A opaque.
 * Most of the swell runs along the tile's +x — the shader turns that onto the
 * wind — and a quarter of it across. Every wave has a whole number of crests
 * across the tile, so the map wraps seamlessly both ways, and the sums are
 * built by angle addition rather than a sine per texel per wave, so a boot
 * pays a few milliseconds for it.
 */
export function rippleTexels(size = RIPPLE_SIZE, seed = 7) {
  const waves = [];
  for (let i = 0; waves.length < RIPPLE_WAVES; i++) {
    const cross = hash2(i, 1, seed) < 0.25;
    const th = (cross ? Math.PI / 2 : 0) + (hash2(i, 2, seed) - 0.5) * (cross ? 1.4 : 1.8);
    const k = 3 + hash2(i, 3, seed) * 13; // 3..16 crests across the tile
    const kx = Math.round(k * Math.cos(th));
    const ky = Math.round(k * Math.sin(th));
    if (kx === 0 && ky === 0) continue;
    // Long waves carry the height, short ones the sparkle.
    waves.push({ kx, ky, a: 1 / Math.pow(Math.hypot(kx, ky), 1.5), ph: hash2(i, 4, seed) * Math.PI * 2 });
  }
  const n = size * size;
  const H = new Float32Array(n), GX = new Float32Array(n), GY = new Float32Array(n);
  const w = (2 * Math.PI) / size;
  const sa = new Float32Array(size), ca = new Float32Array(size);
  const sb = new Float32Array(size), cb = new Float32Array(size);
  for (const v of waves) {
    // sin(a + b) with a along x (phase folded in) and b along y.
    for (let t = 0; t < size; t++) {
      sa[t] = Math.sin(w * v.kx * t + v.ph);
      ca[t] = Math.cos(w * v.kx * t + v.ph);
      sb[t] = Math.sin(w * v.ky * t);
      cb[t] = Math.cos(w * v.ky * t);
    }
    const ax = v.a * v.kx * w, ay = v.a * v.ky * w;
    for (let y = 0; y < size; y++) {
      const sy = sb[y], cy = cb[y];
      for (let x = 0, o = y * size; x < size; x++, o++) {
        const s = sa[x] * cy + ca[x] * sy;
        const c = ca[x] * cy - sa[x] * sy;
        H[o] += v.a * s;
        GX[o] += ax * c;
        GY[o] += ay * c;
      }
    }
  }
  let gMax = 1e-9, hMin = Infinity, hMax = -Infinity;
  for (let o = 0; o < n; o++) {
    gMax = Math.max(gMax, Math.abs(GX[o]), Math.abs(GY[o]));
    hMin = Math.min(hMin, H[o]);
    hMax = Math.max(hMax, H[o]);
  }
  const out = new Uint8Array(n * 4);
  const g = 126 / gMax, h = 255 / (hMax - hMin);
  for (let o = 0; o < n; o++) {
    out[o * 4] = Math.round(128 + GX[o] * g);
    out[o * 4 + 1] = Math.round(128 + GY[o] * g);
    out[o * 4 + 2] = Math.round((H[o] - hMin) * h);
    out[o * 4 + 3] = 255;
  }
  return out;
}

// --- the depth window ----------------------------------------------------------

/** Corners a side of the depth window: ±3 km of the 12 m grid round the camera. */
export const DEPTH_WIN = 512;
// The unit of work: one tile of terrain_core.js's corner cache (CORNER_TILE),
// so a tile the chunk streamer already paid for costs nothing but the copy.
export const DEPTH_TILE = 32;
// Ground heights the window can hold, metres about the waterline. Anything
// deeper is deep water; anything higher is dry land the sea never shows.
export const DEPTH_LO = -24;
export const DEPTH_HI = 8;

/** A ground height as a window byte (0 = deep: open sea until a tile is filled). */
export function encodeGround(h, wl) {
  const t = (h - wl - DEPTH_LO) / (DEPTH_HI - DEPTH_LO);
  return t <= 0 ? 0 : t >= 1 ? 255 : Math.round(t * 255);
}

/** A window byte back to a ground height (metres, absolute). */
export function decodeGround(b, wl) {
  return wl + DEPTH_LO + (b / 255) * (DEPTH_HI - DEPTH_LO);
}

// The ground's steepness rides beside its height, so the shader can tell how
// far a patch of water is from its shore (depth over slope): the surf is a
// band of so many metres along every coast, a steep one or a tidal flat. Kept
// as a square root, for resolution where the flats are.
export const SLOPE_MAX = 0.5;
/** A slope (rise over run) as a window byte. */
export function encodeSlope(g) {
  const t = g / SLOPE_MAX;
  return t <= 0 ? 0 : t >= 1 ? 255 : Math.round(Math.sqrt(t) * 255);
}
/** A window byte back to a slope. */
export function decodeSlope(b) {
  const t = b / 255;
  return t * t * SLOPE_MAX;
}

const tileKey = (tx, tz) => (tx + 32768) * 65536 + (tz + 32768);

/**
 * The ground under the sea round the camera, as texels — its height in the
 * first byte and its slope in the second: corner
 * (ix, iz) of the 12 m grid lives at texel (ix mod size, iz mod size), so the
 * shader samples it straight from the world position with the texture set to
 * repeat, and a window that slides along rewrites only the tiles it slid
 * onto. A tile that enters is wiped to deep water first — its slot still
 * holds whatever lay on the far side of the window — and filled in turn,
 * nearest the camera first.
 */
export class DepthWindow {
  constructor(field, size = DEPTH_WIN, stride = 2) {
    this.field = field;
    this.size = size;
    this.stride = stride; // bytes a texel (2 for RG8, 4 for RGBA on WebGL1)
    this.tiles = size / DEPTH_TILE;
    this.data = new Uint8Array(size * size * stride);
    this.tx0 = null; // the window's low tile, in corner-cache tiles
    this.tz0 = null;
    this.filled = new Set();
    this.queue = []; // [tx, tz], nearest the camera first
    this.dirty = false;
  }

  /** Centre the window on the camera at (x, z). True if it moved. */
  aim(x, z) {
    const cs = this.field.cellSize;
    const ctx = Math.floor(Math.floor(x / cs) / DEPTH_TILE);
    const ctz = Math.floor(Math.floor(z / cs) / DEPTH_TILE);
    const half = this.tiles >> 1;
    const tx0 = ctx - half, tz0 = ctz - half;
    if (tx0 === this.tx0 && tz0 === this.tz0) return false;
    this.tx0 = tx0;
    this.tz0 = tz0;
    const keep = new Set();
    const want = [];
    for (let tz = tz0; tz < tz0 + this.tiles; tz++) {
      for (let tx = tx0; tx < tx0 + this.tiles; tx++) {
        const k = tileKey(tx, tz);
        if (this.filled.has(k)) keep.add(k);
        else {
          this.clearTile(tx, tz);
          want.push([tx, tz]);
        }
      }
    }
    this.filled = keep;
    const d = (t) => (t[0] - ctx) * (t[0] - ctx) + (t[1] - ctz) * (t[1] - ctz);
    this.queue = want.sort((a, b) => d(a) - d(b));
    return true;
  }

  clearTile(tx, tz) {
    const M = this.size - 1, st = this.stride;
    const ix0 = (tx * DEPTH_TILE) & M;
    for (let j = 0; j < DEPTH_TILE; j++) {
      const row = ((tz * DEPTH_TILE + j) & M) * this.size;
      this.data.fill(0, (row + ix0) * st, (row + ix0 + DEPTH_TILE) * st);
    }
    this.dirty = true;
  }

  fillTile(tx, tz) {
    const f = this.field, wl = f.waterLevel;
    const M = this.size - 1, st = this.stride;
    const k = 1 / (2 * f.cellSize);
    for (let j = 0; j < DEPTH_TILE; j++) {
      const iz = tz * DEPTH_TILE + j;
      const row = (iz & M) * this.size;
      for (let i = 0; i < DEPTH_TILE; i++) {
        const ix = tx * DEPTH_TILE + i;
        const o = (row + (ix & M)) * st;
        this.data[o] = encodeGround(f.cornerHeight(ix, iz), wl);
        // Central differences across the neighbouring corners.
        const gx = (f.cornerHeight(ix + 1, iz) - f.cornerHeight(ix - 1, iz)) * k;
        const gz = (f.cornerHeight(ix, iz + 1) - f.cornerHeight(ix, iz - 1)) * k;
        this.data[o + 1] = encodeSlope(Math.sqrt(gx * gx + gz * gz));
      }
    }
    this.filled.add(tileKey(tx, tz));
    this.dirty = true;
  }

  /** Fill waiting tiles for up to `budgetMs`; returns how many landed. */
  pump(budgetMs) {
    const t0 = performance.now();
    let n = 0;
    while (this.queue.length) {
      const [tx, tz] = this.queue.shift();
      this.fillTile(tx, tz);
      n++;
      if (performance.now() - t0 >= budgetMs) break;
    }
    return n;
  }

  /** Share of the window's tiles within `r` tiles of its middle that are filled. */
  coverage(r = this.tiles >> 1) {
    if (this.tx0 === null) return 0;
    const cx = this.tx0 + (this.tiles >> 1), cz = this.tz0 + (this.tiles >> 1);
    let all = 0, have = 0;
    for (let tz = cz - r; tz < cz + r; tz++) {
      for (let tx = cx - r; tx < cx + r; tx++) {
        all++;
        if (this.filled.has(tileKey(tx, tz))) have++;
      }
    }
    return have / all;
  }

  /** The texel holding corner (ix, iz): [height byte, slope byte] (tests). */
  texel(ix, iz) {
    const M = this.size - 1;
    const o = ((iz & M) * this.size + (ix & M)) * this.stride;
    return [this.data[o], this.data[o + 1]];
  }
}

// --- the look ------------------------------------------------------------------

// The deep water's own colour: the classic sea's blue, as it read with the
// dark slab under it — the game's ocean, only now it has a bottom to lose.
const DEEP = new THREE.Color(0x2269ad);
// What the shallows are laid over: the beach sand the terrain paints its
// waterline with, or the land theaters' lake-shore mud (world.js PAL).
const BOTTOM_SAND = new THREE.Color().setRGB(0.83, 0.76, 0.44, THREE.LinearSRGBColorSpace);
const BOTTOM_MUD = new THREE.Color().setRGB(0.6, 0.53, 0.37, THREE.LinearSRGBColorSpace);
// Extinction per metre of light path, red first: the sand goes turquoise in
// the first metre, teal by three and is gone into the blue by eight.
const ABSORB = new THREE.Vector3(0.45, 0.16, 0.11);
const FOAM = new THREE.Color(0xf2f7f7);
// Depth effects fade out between these distances (m, level): past the second
// the window may not hold the ground, and the ground isn't drawn there either.
const FADE = [2100, 2550];
// Fresnel: water reflects 2 % looking straight down, all of it at the
// horizon — capped, so the far sea keeps a little of its blue.
const FRESNEL = [0.02, 0.82];
// The glint: its colour (the key light's, times this) and how tight it is,
// near and far — the ripples average out with distance, so the far sea's
// highlight is broadened into a path rather than a pinpoint.
const SPEC = 0.05;
const SHINE = [1600, 300];
// The swell's steepness in fair weather: a light breeze, not a gale.
const CHOP = 0.65;
// A lake's surf, against the sea's (1): its shore laps rather than breaks.
const LAKE_SURF = 0.3;
// The swell: the two ripple layers' tile sizes (m) and drift speeds (m/s).
const RIPPLE_TILE = [58, 17];
const RIPPLE_DRIFT = [1.1, 0.75];
// How much of the per-frame budget (ms) the depth window may spend filling.
const FILL_BUDGET_MS = 1;
// Re-upload the window at most this often (s) while it is still filling.
const UPLOAD_EVERY = 0.12;

const WATER_VERT_PARS = /* glsl */ `
varying vec3 vWaterPos;
`;

const WATER_FRAG_PARS = /* glsl */ `
uniform sampler2D tRipple;
uniform sampler2D tGround;
uniform vec4 uGroundWin;   // x: 1 / cell size, y: 1 / window texels, z: DEPTH_LO, w: DEPTH_HI - DEPTH_LO
uniform float uSlopeMax;
uniform float uTime;
uniform vec4 uSwell;       // xy: wind direction (unit), z: chop, w: the cross sea's share
uniform vec3 uBottom;
uniform vec3 uAbsorb;
uniform vec3 uFoam;
uniform vec3 uSkyUp;
uniform vec3 uSkyHorizon;
uniform vec4 uFade;        // xy: depth effects fade (m); zw: Fresnel floor and cap
uniform vec4 uShine;       // xy: shininess near and far; z: glint strength; w: glint cap
uniform float uSurf;       // how much the surf rolls: a sea's shore, or a lake's
varying vec3 vWaterPos;
`;

// The body of the water: depth from the window, the swell's slope, foam, and
// the colour the sun and sky fill will light. Leaves wN (world normal), wFoam
// and wDist for the stages below.
const WATER_BODY = /* glsl */ `
  vec3 wP = vWaterPos;
  vec3 wToEye = cameraPosition - wP;
  float wDist = length(wToEye);
  vec3 wV = wToEye / max(wDist, 1e-3);
  float wFlat = length(wToEye.xz);
  // Ripples: the swell's slopes at two scales, turned onto the wind and
  // drifting with it; tilted by their slopes (a rotated map's slope turns
  // back with the transpose). Far off they calm — the map's own mips average
  // them, and they're thinned here as well, so the sea doesn't boil.
  vec2 wd = uSwell.xy;
  vec2 wq = vec2(dot(wP.xz, wd), dot(wP.xz, vec2(-wd.y, wd.x)));
  vec4 r1 = texture2D(tRipple, wq / ${RIPPLE_TILE[0].toFixed(1)} - vec2(uTime * ${(RIPPLE_DRIFT[0] / RIPPLE_TILE[0]).toFixed(5)}, 0.0));
  vec2 s1 = r1.rg - 0.5;
  vec2 slope = vec2(wd.x * s1.x - wd.y * s1.y, wd.y * s1.x + wd.x * s1.y);
#ifdef WATER_LITE
  // Phones and software GL: the one swell, its height standing in for the
  // cross sea's where the surf wants breaking up.
  vec4 r2 = r1.bbbb;
#else
  // The cross sea: a finer chop 45 degrees off the wind.
  vec2 we = vec2(wd.x - wd.y, wd.x + wd.y) * 0.70710678;
  vec2 wr = vec2(dot(wP.xz, we), dot(wP.xz, vec2(-we.y, we.x)));
  vec4 r2 = texture2D(tRipple, wr / ${RIPPLE_TILE[1].toFixed(1)} - vec2(uTime * ${(RIPPLE_DRIFT[1] / RIPPLE_TILE[1]).toFixed(5)}, uTime * 0.013));
  vec2 s2 = r2.rg - 0.5;
  slope += vec2(we.x * s2.x - we.y * s2.y, we.y * s2.x + we.x * s2.y) * uSwell.w;
#endif
  slope *= uSwell.z * mix(1.0, 0.3, smoothstep(60.0, 2200.0, wDist));
  // How deep it is here: the ground under the water, from the window, faded
  // to open sea toward the window's edge (where the ground stops being drawn).
  float wNear = 1.0 - smoothstep(uFade.x, uFade.y, wFlat);
  vec2 wG = texture2D(tGround, (wP.xz * uGroundWin.x + 0.5) * uGroundWin.y).rg;
  float wGround = uGroundWin.z + wG.r * uGroundWin.w;
  float wDepth = max(mix(-uGroundWin.z, -wGround, wNear), 0.0);
  // …and about how far it is to the shore: depth over the ground's slope.
  float wShore = wDepth / max(wG.g * wG.g * uSlopeMax, 0.004);
  // The light's path through the water: down to the bottom and back up the
  // refracted view ray (Snell's window keeps even a grazing look under 1.5x).
  float wCosT = sqrt(max(1.0 - (1.0 - wV.y * wV.y) * 0.5653, 0.0)); // 1 / 1.33^2
  vec3 wT = exp(-uAbsorb * wDepth * (1.0 + 1.0 / max(wCosT, 0.3)));
  vec3 wBody = uBottom * wT + diffuseColor.rgb * (1.0 - wT);
  // Surf: a band some twenty metres wide along every shore, its lines
  // rolling in to the beach a dozen metres apart and broken up by the swell,
  // and the swash itself white along the waterline (a lake's a thinner lap).
  float wZone = (1.0 - smoothstep(3.0, 24.0, wShore)) * wNear;
  float wRoll = 0.5 + 0.5 * sin(wShore * 0.52 + uTime * 1.8 + (r1.b + r2.b) * 3.0);
  float wFoam = wZone * smoothstep(0.66, 0.92, wRoll * 0.62 + r2.b * 0.38) * (0.35 + 0.65 * wZone) * uSurf;
  wFoam = max(wFoam, (1.0 - smoothstep(0.3 + 0.3 * uSurf, 1.0 + 1.6 * uSurf, wShore + r1.b * 1.4)) * wNear);
  diffuseColor.rgb = mix(wBody, uFoam, wFoam);
  vec3 wN = normalize(vec3(-slope.x, 1.0, -slope.y));
  wN = normalize(mix(wN, vec3(0.0, 1.0, 0.0), wFoam * 0.8));
`;

const WATER_NORMAL = /* glsl */ `
  normal = normalize((viewMatrix * vec4(wN, 0.0)).xyz);
`;

const WATER_GLINT = /* glsl */ `
  material.specularShininess = mix(uShine.x, uShine.y, smoothstep(0.0, 2500.0, wDist));
  material.specularColor = vec3(uShine.z * (1.0 - wFoam));
`;

// The sky over it all, by Fresnel: the lit body underfoot, the sky's own
// colour toward the horizon; the glint on top.
const WATER_OUT = /* glsl */ `
  float wCosV = clamp(dot(wN, wV), 0.0, 1.0);
  float wF = min(uFade.z + (1.0 - uFade.z) * pow(1.0 - wCosV, 5.0), uFade.w) * (1.0 - wFoam);
  vec3 wR = reflect(-wV, wN);
  vec3 wSky = mix(uSkyHorizon, uSkyUp, smoothstep(0.0, 0.5, wR.y));
  vec3 wGlint = reflectedLight.directSpecular + reflectedLight.indirectSpecular;
  wGlint = uShine.w * (1.0 - exp(-wGlint / uShine.w));
  vec3 outgoingLight = mix(reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance, wSky, wF) + wGlint;
`;

const OUTGOING =
  "vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;";

/** `src` with `marker` swapped for `code`; a marker three moved is an error, not a silent flat sea. */
function inject(src, marker, code) {
  if (!src.includes(marker)) throw new Error(`water shader: three.js has no "${marker}" to hook`);
  return src.replace(marker, code);
}

/**
 * The sea's shader hooks, applied to a MeshPhongMaterial's source (exported
 * so the unit tests can hold them against three's own Phong chunks).
 */
export function waterShader(sh, uniforms) {
  Object.assign(sh.uniforms, uniforms);
  sh.vertexShader = inject(sh.vertexShader, "#include <common>", "#include <common>\n" + WATER_VERT_PARS);
  sh.vertexShader = inject(
    sh.vertexShader,
    "#include <begin_vertex>",
    "#include <begin_vertex>\nvWaterPos = (modelMatrix * vec4(transformed, 1.0)).xyz;",
  );
  sh.fragmentShader = inject(sh.fragmentShader, "#include <common>", "#include <common>\n" + WATER_FRAG_PARS);
  sh.fragmentShader = inject(sh.fragmentShader, "#include <color_fragment>", "#include <color_fragment>\n" + WATER_BODY);
  sh.fragmentShader = inject(sh.fragmentShader, "#include <normal_fragment_maps>", WATER_NORMAL);
  sh.fragmentShader = inject(sh.fragmentShader, "#include <lights_phong_fragment>", "#include <lights_phong_fragment>\n" + WATER_GLINT);
  sh.fragmentShader = inject(sh.fragmentShader, OUTGOING, WATER_OUT);
  return sh;
}

/**
 * How much sea this machine draws: "hq" under tier 2's film light, "full" on
 * any other GPU, "lite" on a phone or a software rasterizer, where every
 * texture fetch across a screenful of water is paid for in full — one swell
 * instead of two, and little of the anisotropic filtering that keeps the far
 * swell from combing. `cap` is how hard a glint may burn: it rolls off toward
 * that (in the key light's units) instead of clipping to a flat white blot,
 * since the classic tier has no film curve to do it; tier 2 gets the headroom
 * to bloom.
 */
export const WATER_QUALITY = {
  lite: { layers: 1, aniso: 2, cap: 1.6 },
  full: { layers: 2, aniso: 16, cap: 1.6 },
  hq: { layers: 2, aniso: 16, cap: 6 },
};

/**
 * Lay the sea into `scene` at `field`'s waterline. It is lit by the scene's
 * own lights and reflects the scene's sky (its background overhead, its fog
 * colour at the horizon), so it needs no handle on buildSky. `renderer` says
 * whether the window can be a two-byte texture (WebGL2); `quality` is a
 * WATER_QUALITY key. Returns { mesh, window, uniforms, update(camera, state) }
 * — main.js calls update right before each render.
 */
export function buildWater(scene, field, renderer = null, { quality = "full" } = {}) {
  const q = WATER_QUALITY[quality] ?? WATER_QUALITY.full;
  const webgl2 = renderer?.capabilities?.isWebGL2 ?? true;
  const win = new DepthWindow(field, DEPTH_WIN, webgl2 ? 2 : 4);
  const ground = new THREE.DataTexture(win.data, DEPTH_WIN, DEPTH_WIN, webgl2 ? THREE.RGFormat : THREE.RGBAFormat);
  ground.wrapS = ground.wrapT = THREE.RepeatWrapping;
  ground.magFilter = ground.minFilter = THREE.LinearFilter;
  ground.generateMipmaps = false;
  ground.unpackAlignment = 1;
  ground.needsUpdate = true;

  const ripple = new THREE.DataTexture(rippleTexels(), RIPPLE_SIZE, RIPPLE_SIZE, THREE.RGBAFormat);
  ripple.wrapS = ripple.wrapT = THREE.RepeatWrapping;
  ripple.magFilter = THREE.LinearFilter;
  ripple.minFilter = THREE.LinearMipmapLinearFilter;
  ripple.generateMipmaps = true;
  // Anisotropy keeps the swell from combing into bands toward the horizon.
  ripple.anisotropy = Math.min(q.aniso, renderer?.capabilities?.getMaxAnisotropy?.() ?? 1);
  ripple.needsUpdate = true;

  const land = field.theater === "land" || field.theater === "armada";
  // The land theater's water is all lakes: a lap at the shore, not surf. The
  // Gulf's sea breaks on its beaches like any other.
  const lakes = field.theater === "land";
  const uniforms = {
    tRipple: { value: ripple },
    tGround: { value: ground },
    uGroundWin: { value: new THREE.Vector4(1 / field.cellSize, 1 / DEPTH_WIN, DEPTH_LO, DEPTH_HI - DEPTH_LO) },
    uSlopeMax: { value: SLOPE_MAX },
    uTime: { value: 0 },
    uSwell: { value: new THREE.Vector4(1, 0, 1, 0.5) },
    uBottom: { value: (land ? BOTTOM_MUD : BOTTOM_SAND).clone() },
    uAbsorb: { value: ABSORB.clone() },
    uFoam: { value: FOAM.clone() },
    uSkyUp: { value: new THREE.Color(0x8ec9ff) },
    uSkyHorizon: { value: new THREE.Color(0x9fd0f5) },
    uFade: { value: new THREE.Vector4(FADE[0], FADE[1], FRESNEL[0], FRESNEL[1]) },
    uShine: { value: new THREE.Vector4(SHINE[0], SHINE[1], SPEC, q.cap) },
    uSurf: { value: lakes ? LAKE_SURF : 1 },
  };
  const mat = new THREE.MeshPhongMaterial({ color: DEEP, specular: 0xffffff, shininess: SHINE[0] });
  // Drawn where the old sea was: first after the backdrop (stars, the sun's
  // glow, tier 2's sky; world.js buildSky), so everything see-through lies
  // over it — but opaque, writing depth, and never blended.
  mat.transparent = true;
  mat.blending = THREE.NoBlending;
  if (q.layers < 2) mat.defines = { WATER_LITE: 1 };
  mat.onBeforeCompile = (sh) => waterShader(sh, uniforms);
  mat.customProgramCacheKey = () => `va-water-${q.layers}`;

  const radius = field.radius;
  const mesh = new THREE.Mesh(new THREE.CircleGeometry(radius * 2.2, 48), mat);
  mesh.name = "water";
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = field.waterLevel;
  mesh.renderOrder = -8;
  mesh.frustumCulled = false;
  scene.add(mesh);

  let uploadAt = -1e9;
  const clock = () => (typeof performance !== "undefined" ? performance.now() / 1000 : 0);
  return {
    mesh,
    window: win,
    uniforms,
    /**
     * Follow the camera: slide the window, fill a little of it, and take the
     * sky's light. `wind` is the round's ({x, z}); `storm` how deep the
     * camera sits in a storm cell (storm.js insideK, 0..1).
     */
    update(camera, { wind = null, storm = 0 } = {}) {
      const p = camera.position;
      win.aim(p.x, p.z);
      const filled = win.pump(FILL_BUDGET_MS);
      const now = clock();
      if (win.dirty && (win.queue.length === 0 || now - uploadAt >= UPLOAD_EVERY || filled === 0)) {
        ground.needsUpdate = true;
        win.dirty = false;
        uploadAt = now;
      }
      // The swell's clock: page time, folded once a day so a highp float
      // still resolves a millimetre of drift after a long night's flying.
      uniforms.uTime.value = now % 86400;
      // The swell runs with the round's wind when it blows, and the chop
      // climbs inside a storm cell (storm.js insideK).
      if (wind && (wind.x || wind.z)) {
        const l = Math.hypot(wind.x, wind.z);
        uniforms.uSwell.value.x = wind.x / l;
        uniforms.uSwell.value.y = wind.z / l;
      }
      uniforms.uSwell.value.z = CHOP * (1 + 1.3 * storm);
      // The sky it reflects: the palette overhead, the haze at the horizon —
      // the same haze the fog greys the far sea into.
      if (scene.background?.isColor) uniforms.uSkyUp.value.copy(scene.background);
      if (scene.fog) uniforms.uSkyHorizon.value.copy(scene.fog.color);
    },
  };
}
